import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import { Observable, combineLatest, BehaviorSubject, Subject } from 'rxjs';
import { map as rxMap } from 'rxjs/operators';
import { ICellRendererParams, GridOptions } from 'ag-grid-community';
import {
    CovanceGridColumn, dateTimeRenderer, dateTimeComparator, MulticellParams, MulticellTooltip,
    MulticellItem, MulticellAgRendererComponent, CovanceAgGridHelper
} from '../../covance-ag-grid';
import { LabTestResult } from '../../lab-test-results';
import { comparators } from '../../core';
import { chain, sortBy, isNil, isEmpty, flatMap, map, partition, flatten } from 'lodash';
import { LabTestResultGridRowItem } from '../grid-row-item';
import { OnScreenResultsRenderer } from '../on-screen-results-renderer';
import { groupingOptions, GroupingOptionItem } from './../../grouping-selector/grouping-options';
import { OsrSubjectColumnRendererComponent } from './osr-subject-columns-renderer.component';
import { TestResultsParams, TestResultAgRendererComponent } from '../test-result-ag-renderer.component';
import { GridLegendItem } from '../../legend';
import { createLegend } from '../osr-legend-generator';
import { StandardTestResultsData } from './standard-test-result-data';
import { StandardTestResultHelpers } from './standard-test-result-helpers';
import { TermsStorage } from '../../corporate/terms-storage';
import { LabTestsGridColumns } from '../../subjects/lab-tests-grid-columns';
import { covanceExcelStyles } from '../../covance-ag-grid/excel-styles';
import { SortModel } from '../../covance-ag-grid/ag-export.component';
import { StandardTestResultsAgGroupRendererComponent } from './standard-test-results-ag-group-renderer.component';
import { DataRevisionIndicator } from '../../shared/data-revision/data-revision-indicator';
import { StandardTestResultsSort, StandardTestResultSortableRow } from './standard-test-results-sort';
import { TestTitleAgRendererComponent } from './test-title-ag-renderer.component';
import { TestVisitAgRendererComponent } from './test-visit-ag-renderer.component';
import { LAB_TEST_RESULTS_LINK_BUILDER, LabTestResultsLinkBuilder } from '../../subjects/lab-test-results-link-builder';
import { SITE_LAB_RESULTS_STORAGE, SiteTestDataStorage } from './site-test-data-storage';

export interface StandardTestResultsItem extends ICellRendererParams {
    data: StandardTestResultsData;
}

export interface StandardTestResultsGridColumn extends CovanceGridColumn {
    hasDataRevision?: (labTestResult: LabTestResult) => boolean;
    getDataRevisionMessage?: (dataRevisionIndicator: DataRevisionIndicator) => string;
}

@Component({
    selector: 'lab-tests',
    template: require('./standard-test-results.component.html'),
    styles: [require('./standard-test-results.component.scss')],
    providers: [StandardTestResultHelpers, ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class StandardTestResultsComponent {

    public get disclaimer() {
        return this.termsStorage.osrDisclaimerText;
    }
    public columns: StandardTestResultsGridColumn[];
    public commonColumns: StandardTestResultsGridColumn[];
    public exportColumns: StandardTestResultsGridColumn[];
    public results: Observable<StandardTestResultsData[]>;
    public exportResults: LabTestResult[];
    public groupOptions: GroupingOptionItem[] = [groupingOptions.none, groupingOptions.subject, groupingOptions.visit];
    public groupBy: GroupingOptionItem = this.groupOptions[0];
    public legend: GridLegendItem[] = [];
    public exportGrid: GridOptions = {
        excelStyles: covanceExcelStyles
    };
    public gridOptions: GridOptions = {
        excelStyles: covanceExcelStyles,
        groupRowRendererParams: { suppressCount: true },
        groupRowInnerRendererFramework: StandardTestResultsAgGroupRendererComponent,
        animateRows: false,
        rowClass: 'ag-row-group-ellipsis'
    };
    public readonly currentGrouping = new Subject<GroupingOptionItem>();

    private hiddenColumns: string[] = [];
    private readonly subjectFieldName = 'initials';
    private gridSortModel: Object;

    constructor(
        @Inject(SITE_LAB_RESULTS_STORAGE) labTestDataSource: SiteTestDataStorage,
        @Inject(LAB_TEST_RESULTS_LINK_BUILDER) linkBuilder: LabTestResultsLinkBuilder,
        private onScreenResultsRenderer: OnScreenResultsRenderer,
        private helper: StandardTestResultHelpers,
        private labTestsGridColumns: LabTestsGridColumns,
        private termsStorage: TermsStorage,
        private gridHelper: CovanceAgGridHelper,
        private standardTestResultsSort: StandardTestResultsSort,
    ) {
        this.commonColumns = [
            {
                headerName: 'Collected Time',
                field: 'visitCollectionDate',
                cellRenderer: (params: LabTestResultGridRowItem) => {
                    return dateTimeRenderer({ value: params.data && params.data.visitCollectionDate });
                },
                getQuickFilterText: (params: LabTestResultGridRowItem) => dateTimeRenderer({ value: params.data && params.data.visitCollectionDate })
            },
            {
                headerName: 'Accession',
                field: 'accessionNumber'
            },
            {
                headerName: 'Test Group',
                field: 'testGroup'
            },
            {
                headerName: 'Test',
                field: 'test',
                cellRendererFramework: TestTitleAgRendererComponent
            }
        ];
        this.columns = [
            {
                headerName: 'Subject',
                field: this.subjectFieldName,
                valueGetter: (params: StandardTestResultsItem) => this.helper.getSubjectInfo(params, linkBuilder.getSubjectLink(params)), 
                cellRendererFramework: OsrSubjectColumnRendererComponent,
                comparator: this.helper.sortSubjects,
                hasDataRevision: (data: LabTestResult) => data && data.hasSubjectDataRevision,
                getDataRevisionMessage: (dataRevisionIndicator: DataRevisionIndicator) => dataRevisionIndicator.subjectDataRevisionMessage

            },
            {
                headerName: 'Visit',
                field: 'visitName',
                valueGetter: (params: StandardTestResultsItem) => {
                    if (!params.data) {
                        return null;
                    }

                    return {
                        sortableName: this.standardTestResultsSort.getVisitName(params),
                        toString: () => params.data.visitName
                    } as StandardTestResultSortableRow;
                },
                cellRendererFramework: TestVisitAgRendererComponent,
                comparator: (a: StandardTestResultSortableRow, b: StandardTestResultSortableRow) => this.standardTestResultsSort.sortEntities(a ? a.sortableName : null, b ? b.sortableName : null),
                hasDataRevision: (data: LabTestResult) => data && data.hasVisitDataRevision,
                getDataRevisionMessage: (dataRevisionIndicator: DataRevisionIndicator) => dataRevisionIndicator.visitDataRevisionMessage
            },
            ...this.commonColumns,
            {
                headerName: 'Value',
                field: 'value',
                valueGetter: (params: StandardTestResultsItem) => {
                    const renderData = this.onScreenResultsRenderer.getLabTestResultsRenderData(params.data ? params.data.labTestResults : []);

                    return {
                        labTestResults: renderData,
                        toString: () => renderData.map(d => d.text).join('\n\n')
                    } as TestResultsParams;
                },
                cellRendererFramework: TestResultAgRendererComponent,
                getLineCount: (entity: StandardTestResultsData) => {
                    return entity.labTestResults.length;
                },
                getRowHeight: (entity: StandardTestResultsData) => {
                    //the value of lineHeight should match the one in grid.scss
                    const lineHeight = 23;

                    return entity.labTestResults.length * lineHeight;
                },
                comparator: (a: TestResultsParams, b: TestResultsParams) =>
                    comparators.stringComparator(this.helper.getDisplayValue(a), this.helper.getDisplayValue(b))
            },
            {
                headerName: 'Flags',
                field: 'flags',
                comparator: (flagsA: MulticellParams, flagsB: MulticellParams): number => {
                    const a = flagsA ? flagsA.toString() : null;
                    const b = flagsB ? flagsB.toString() : null;

                    return comparators.stringComparator(a, b);
                },
                valueGetter: (params: StandardTestResultsItem) => {
                    if (!params.data) {
                        return null;
                    }
                    const items = params.data.labTestResults.map(result => {
                        const tooltips = result.flags.map(flag => {
                            return {
                                title: flag.nameUpperCase,
                                content: flag.category
                            } as MulticellTooltip;
                        });

                        return {
                            displayValue: this.labTestsGridColumns.sortFlags(result.flags),
                            tooltips: sortBy(tooltips, x => x.content)
                        } as MulticellItem;
                    }
                    );
                    return {
                        alwaysShowTooltip: true,
                        items: items,
                        toString: () => items.map(i => i.displayValue).join('\n')
                    } as MulticellParams;
                },
                cellRendererFramework: MulticellAgRendererComponent,
                cellClass: 'flags-exist'
            },
            {
                headerName: 'Comments',
                field: 'comments',
                valueGetter: (params: StandardTestResultsItem) => {
                    const comments = params.data ? params.data.labTestResults.map(d => d.comments) : [];
                    return {
                        alwaysShowTooltip: false,
                        items: comments.map(comment => {
                            return {
                                displayValue: comment,
                                tooltips: [{ content: comment }]
                            } as MulticellItem;
                        }),
                        toString: () => comments.join('\n')
                    } as MulticellParams;
                },
                comparator: (a: MulticellParams, b: MulticellParams) => {
                    return comparators.stringComparator(a.toString(), b.toString());
                },
                cellRendererFramework: MulticellAgRendererComponent
            },
            {
                headerName: 'Range',
                field: 'results',
                valueGetter: this.helper.getResultRenderer(r => r.range)
            },
            {
                headerName: 'Units',
                field: 'unit',
                valueGetter: this.helper.getResultRenderer(r => r.unit || '')
            }
        ];
        this.hiddenColumns = this.columns.filter(col => col.hide).map(col => col.headerName);
        var resultsData = labTestDataSource.getData
            .pipe(
                rxMap((data: LabTestResult[]): StandardTestResultsData[] => {

                    if (!data) {
                        return null;
                    }

                    this.exportColumns = this.getExportColumns(data);
                    const splitResult = partition(data, (a: LabTestResult) => a.isCombinedResult && !isNil(a.visitTestId));
                    const combinedLabResults = splitResult[0];
                    const usualResults = splitResult[1];

                    const items = usualResults.map((item: LabTestResult, itemIndex: number) => {
                        return new StandardTestResultsData([item], itemIndex);
                    });
                    const ind = usualResults.length;
                    let index = ind - 1;

                    const combinedLabResultsData = chain(combinedLabResults)
                        .groupBy(r => helper.getCombinedLabTestResultsUniqKey(r))
                        .map((combinedResults: LabTestResult[]) => new StandardTestResultsData(combinedResults))
                        .value();
                    items.push(...combinedLabResultsData);

                    items.sort((a: StandardTestResultsData, b: StandardTestResultsData) => this
                        .defaultStandardTestResultSort(a, b));
                    items.forEach((item: StandardTestResultsData) => {
                        index++;
                        item.setLabTestResultExportSortId(index);
                    });

                    this.exportResults = flatten(items.map(r => r.labTestResults));
                    this.legend = createLegend(data);

                    return items;
                })
            );

        this.results = combineLatest(resultsData, this.currentGrouping)
        .pipe(
            rxMap(([resultData, currentGrouping]) => {
                const groupedBy = currentGrouping.columnTitle;
                const defaultStandardTestResultSort = this.isSortModelEmpty()
                    ? null
                    : this.defaultStandardTestResultSort;
                const groupResult = this.gridHelper.groupData(groupedBy, resultData, this.columns, this.hiddenColumns, defaultStandardTestResultSort);
                this.columns = groupResult.columns;
                var groupedData = groupResult.data;
                if (!isEmpty(groupedData) && currentGrouping === groupingOptions.none && this.isSortModelEmpty()) {
                    return groupedData.sort(
                        (a, b) => this.helper.sortLabTestResultData(a.labTestResults[0], b.labTestResults[0]));
                }

                return groupedData;
            })
        );
    }

    public onGroupingChange(option: GroupingOptionItem) {
        this.currentGrouping.next(option);
    }

    public handleSubjectSort = (sortModels: SortModel[]): SortModel[] => {
        return flatMap(sortModels, sortModel => {
            if (sortModel.colId === this.subjectFieldName) {
                return map(this.labTestsGridColumns.getSubjectExportColumns(), (subjectColumn: CovanceGridColumn): SortModel => {
                    return { colId: subjectColumn.field, sort: sortModel.sort };
                });
            }
            return sortModel;
        });
    }

    public onDisplayedColumnsChanged() {
        // restore the current sort after group/ungroup
        this.restoreSortModel();
    }

    public onGridSortChanged() {
        this.saveSortModel();
    }

    public onGridQuickFilterChanged(filter: string) {
        if (this.exportGrid) {
            this.exportGrid.quickFilterText = filter;
        }
    }

    private readonly defaultStandardTestResultSort = (a: StandardTestResultsData, b: StandardTestResultsData) => this.helper.sortLabTestResultData(a ? a.labTestResults[0] : null, b ? b.labTestResults[0] : null);

    private getExportColumns(res: LabTestResult[]): CovanceGridColumn[] {
        return [
            ...this.labTestsGridColumns.getSubjectExportColumns(),
            {
                headerName: 'Visit',
                field: 'visitNameToDisplay',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }

                    return data.visitName || '';
                }
            },
            {
                noExport: true,
                headerName: 'Visit',
                field: 'visitName',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }

                    return this.standardTestResultsSort.getVisitName(params);
                },
                comparator: (a, b) => this.standardTestResultsSort.sortEntities(a, b)
            },
            {
                headerName: 'Visit Data Revision',
                field: 'hasVisitDataRevision',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getDataRevision(params, labTest => labTest.hasVisitDataRevision)
            },
            ...this.commonColumns,
            {
                headerName: 'Administrative Question',
                field: 'administrativeQuestion',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getAdminQuestionRenderer(params)
            },
            {
                headerName: 'Value',
                field: 'value',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    if (!params || !params.data) {
                        return null;
                    }
                    const data = params.data;
                    if (data.isCancelled) {
                        return '';
                    }
                    const result = data.currentResult;
                    const stringRes = !isNil(result) ? result.stringResult : '';

                    return stringRes;
                }
            },
            {
                headerName: 'Result Data Revision',
                field: 'hasResultDataRevision',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getDataRevision(params, labTest => labTest.hasResultDataRevision)
            },
            {
                headerName: 'Flags',
                field: 'flags',
                valueGetter: (params: LabTestResultGridRowItem) => this.labTestsGridColumns.getFlags(params)
            },
            {
                headerName: 'Cancellation Reason',
                field: 'cancellationReason',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    if (!params || !params.data) {
                        return null;
                    }

                    return params.data.cancellationReason;
                }
            },
            {
                headerName: 'Comments',
                field: 'commentsToDisplay',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }

                    return data.comments;
                }
            },
            {
                noExport: true,
                headerName: 'Comments',
                field: 'comments',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }
                    return this.standardTestResultsSort.getComments(params);
                },
                comparator: (a, b) => this.standardTestResultsSort.sortEntities(a, b)
            },
            {
                headerName: 'Range',
                field: 'results',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getRangeRendererForTestResultExport(res, params)
            },
            {
                headerName: 'Units',
                field: 'unit',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getUnitsForTestResultExport(res, params)
            }
        ];
    }

    private saveSortModel() {
        if (this.gridOptions.api) {
            this.gridSortModel = this.gridOptions.api.getSortModel();
        }
    }

    private restoreSortModel() {
        if (this.gridSortModel) {
            this.gridOptions.api.setSortModel(this.gridSortModel);
        }
    }

    private isSortModelEmpty(): boolean {
        return !this.gridOptions.api || isEmpty(this.gridOptions.api.getSortModel());
    }
}
